import axios from 'axios'
import { supabase } from '../lib/supabase'
import { getStoredAccountId } from '../utils/accountStorage'
import { showQuotaExceededModal, showFeatureLockedModal } from '../utils/subscriptionModals'
import { sanitizeCaption, sanitizeHexColor, sanitizeInput } from '../utils/sanitization'
import { setVersionFromHeader } from '../hooks/useRelease'

// Create axios instance with base configuration
const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:7071/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Helper: unwrap axios response payload
const unwrap = (promise) => promise.then((response) => response.data)

// Request interceptor to add Supabase JWT token
axiosInstance.interceptors.request.use(
  async (config) => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (session?.access_token) {
        config.headers.Authorization = `Bearer ${session.access_token}`
      }
    } catch (error) {
      console.error('Failed to get auth session:', error)
    }

    try {
      const accountId = getStoredAccountId()
      if (accountId) {
        // Add instagram_account_id as query param for all methods
        config.params = {
          instagram_account_id: accountId,
          ...(config.params || {}),
        }
      }
    } catch (error) {
      console.warn('Failed to inject instagram_account_id', error)
    }

    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor to handle errors and auth redirects
axiosInstance.interceptors.response.use(
  (response) => {
    // Capture API release version from response header
    const version = response.headers['x-release'];
    if (version) {
      setVersionFromHeader(version);
    }
    return response;
  },
  async (error) => {
    if (error.response) {
      const status = error.response.status
      const data = error.response.data

      // Handle 401 Unauthorized - sign out user (but not for token refresh failures)
      if (status === 401 && data?.reason !== 'reconnect_required') {
        await supabase.auth.signOut()
        window.location.href = '/login'
      }

      // Handle 429 Quota Exceeded
      if (status === 429 && data?.quota_type) {
        showQuotaExceededModal({
          quota_type: data.quota_type,
          used: data.used ?? 0,
          limit: data.limit ?? 0,
          unlimited: data.unlimited ?? false,
          resets_at: data.resets_at ?? null,
          message: data.message ?? 'You have reached your quota limit',
        })
      }

      // Handle 403 Feature Locked
      if (status === 403 && data?.feature_id) {
        showFeatureLockedModal({
          feature_id: data.feature_id,
          message: data.message ?? 'This feature is not available in your plan',
        })
      }

      const message =
        data?.message ||
        data?.error ||
        'An error occurred'
      error.message = message
    } else if (error.request) {
      error.message = 'No response from server. Please check your connection.'
    } else {
      error.message = error.message || 'An unexpected error occurred'
    }

    return Promise.reject(error)
  }
)

const fetchImages = async (params = {}) => {
  const data = await unwrap(axiosInstance.get('/images', { params }))
  const images = data?.images ?? []

  return {
    images,
    count: data?.count ?? images.length,
  }
}

const computeImageStats = (images) => {
  const awaitingApproval = images.filter(
    (item) => item.status === 'awaiting_approval'
  ).length
  const approved = images.filter((item) => item.status === 'approved').length
  const rejected = images.filter((item) => item.status === 'rejected').length

  return {
    totalImages: images.length,
    awaitingApproval,
    approved,
    rejected,
  }
}

// Compute story statistics with time-based filtering
const computeStoryStats = async (accountId) => {
  try {
    // Fetch stories by different status types
    // Use /approvals endpoint for approval queue to include both images AND videos
    const [approvalQueueData, scheduledData, postedData] = await Promise.all([
      unwrap(axiosInstance.get('/approvals', { params: { type: 'all', instagram_account_id: accountId, limit: 100 } })),
      unwrap(axiosInstance.get('/stories', { params: { status: 'scheduled', instagram_account_id: accountId } })),
      unwrap(axiosInstance.get('/stories', { params: { status: 'posted', instagram_account_id: accountId } })),
    ])

    // Count both images and videos in the approval queue
    const approvalQueue = approvalQueueData?.items?.length ?? 0
    const scheduled = scheduledData?.stories?.length ?? 0
    const postedStories = postedData?.stories ?? []

    // Calculate currently posted vs expired based on 24h window
    const now = new Date()
    const currentlyPosted = postedStories.filter((story) => {
      if (!story.posted_at) return false
      const postedAt = new Date(story.posted_at)
      const hoursSincePost = (now - postedAt) / (1000 * 60 * 60)
      return hoursSincePost < 24
    }).length

    const expired = postedStories.filter((story) => {
      if (!story.posted_at) return false
      const postedAt = new Date(story.posted_at)
      const hoursSincePost = (now - postedAt) / (1000 * 60 * 60)
      return hoursSincePost >= 24
    }).length

    return {
      approvalQueue,
      scheduled,
      currentlyPosted,
      expired,
    }
  } catch (error) {
    console.error('Failed to compute story stats:', error)
    return {
      approvalQueue: 0,
      scheduled: 0,
      currentlyPosted: 0,
      expired: 0,
    }
  }
}

// API service methods aligned with current backend endpoints
export const api = {
  // Authentication is now handled by Supabase directly in AuthContext
  // No need for login/logout/getCurrentUser endpoints here

  // Images
  getImages: fetchImages,
  getImageById: (id, params = {}) =>
    unwrap(axiosInstance.get(`/images/${id}`, { params })),
  getImageStatus: (imageId, params = {}) =>
    unwrap(axiosInstance.get(`/images/${imageId}/status`, { params })),
  getPendingImages: async (params = {}) => {
    // Fetch both awaiting_approval AND rendering_gif images
    // rendering_gif images stay in approval queue with badge while ACI processes
    if (!params.instagram_account_id) {
      throw new Error('instagram_account_id is required for getPendingImages')
    }

    const [awaitingData, renderingData] = await Promise.all([
      fetchImages({ ...params, status: 'awaiting_approval' }),
      fetchImages({ ...params, status: 'rendering_gif' })
    ])

    // Combine results and remove duplicates (shouldn't happen, but safe)
    const allImages = [...awaitingData.images, ...renderingData.images]
    const imageMap = new Map(allImages.map(img => [img.image_id, img]))

    return Array.from(imageMap.values())
  },
  getApprovals: async (params = {}) => {
    // Fetch unified approval queue (both images and videos)
    // Returns items array with type='image' or type='video' for each item
    const queryParams = {
      type: params.type || 'all',
      limit: params.limit || 50
    }
    if (params.instagram_account_id) {
      queryParams.instagram_account_id = params.instagram_account_id
    }

    const result = await unwrap(axiosInstance.get('/approvals', { params: queryParams }))
    return result
  },
  getProcessingCount: async (accountId) => {
    // Get count of images currently being processed (pending + analyzed)
    // Higher limit to ensure we catch all processing images
    // Note: This fetches images primarily for counting purposes
    if (!accountId) {
      throw new Error('instagram_account_id is required for getProcessingCount')
    }

    const params = {
      instagram_account_id: accountId
    }

    const [pendingData, analyzedData] = await Promise.all([
      fetchImages({ ...params, status: 'pending', limit: 500 }),
      fetchImages({ ...params, status: 'analyzed', limit: 500 })
    ])

    return {
      pending: pendingData.count || 0,
      analyzed: analyzedData.count || 0,
      total: (pendingData.count || 0) + (analyzedData.count || 0)
    }
  },
  getStats: async (images) => {
    const resolvedImages = images ?? (await fetchImages()).images
    return computeImageStats(resolvedImages)
  },
  getStoryStats: async (accountId) => {
    return await computeStoryStats(accountId)
  },

  // Image Reuse
  getArchivedImages: (params = {}) =>
    unwrap(axiosInstance.get('/archive-images', { params })),
  reuseImage: (imageId, payload) => {
    // Sanitize additional instructions if provided
    const sanitizedPayload = {
      ...payload,
      ...(payload.additional_instructions && {
        additional_instructions: sanitizeInput(payload.additional_instructions, 500)
      }),
    }
    return unwrap(axiosInstance.post(`/images/${imageId}/reuse`, sanitizedPayload))
  },

  // Captions
  getCaptions: (params = {}) => unwrap(axiosInstance.get('/captions', { params })),
  approveCaption: (captionId, payload) => {
    // Sanitize caption data before sending
    const sanitizedPayload = {
      ...payload,
      ...(payload.caption_text && { caption_text: sanitizeCaption(payload.caption_text) }),
    }
    return unwrap(axiosInstance.post(`/captions/${captionId}/approve`, sanitizedPayload))
  },
  regenerateCaption: (payload) => {
    // Sanitize caption and feedback data before sending
    const sanitizedPayload = {
      ...payload,
      ...(payload.caption_text && { caption_text: sanitizeCaption(payload.caption_text) }),
      ...(payload.rejection_reason && { rejection_reason: sanitizeInput(payload.rejection_reason, 500) }),
      ...(payload.regeneration_wishes && { regeneration_wishes: sanitizeInput(payload.regeneration_wishes, 500) }),
      ...(payload.text_color_hex && { text_color_hex: sanitizeHexColor(payload.text_color_hex) }),
      ...(payload.text_background_color_hex && { text_background_color_hex: sanitizeHexColor(payload.text_background_color_hex) }),
    }
    return unwrap(axiosInstance.post('/captions/regenerate', sanitizedPayload))
  },

  // Schedule Preview (for approval modal - works for both images and videos)
  getSchedulePreview: (accountId, config = {}) =>
    unwrap(axiosInstance.get('/captions/schedule-preview', {
      params: { instagram_account_id: accountId },
      ...config
    })),

  // Stories
  getStories: (params = {}, config = {}) => unwrap(axiosInstance.get('/stories', { params, ...config })),
  rejectImage: (imageId, payload) => {
    // Sanitize rejection reason
    const sanitizedPayload = {
      ...payload,
      ...(payload.reason && { reason: sanitizeInput(payload.reason, 500) }),
    }
    return unwrap(axiosInstance.post(`/images/${imageId}/reject`, sanitizedPayload))
  },

  // Videos (Approval Queue)
  approveVideo: (videoId, params = {}) => {
    // Approve a video and update its status to APPROVED
    const queryParams = {}
    if (params.instagram_account_id) {
      queryParams.instagram_account_id = params.instagram_account_id
    }
    return unwrap(axiosInstance.post(`/videos/${videoId}/approve`, {}, { params: queryParams }))
  },
  deleteVideo: (videoId, params = {}) => {
    // Delete a video from approval queue
    const queryParams = {}
    if (params.instagram_account_id) {
      queryParams.instagram_account_id = params.instagram_account_id
    }
    return unwrap(axiosInstance.delete(`/videos/${videoId}`, { params: queryParams }))
  },

  rescheduleStory: (storyId, payload) =>
    unwrap(axiosInstance.put(`/stories/${storyId}/reschedule`, payload)),
  bulkRescheduleStories: (instagramAccountId, reschedules) =>
    unwrap(axiosInstance.put('/stories/bulk-reschedule', {
      instagram_account_id: instagramAccountId,
      reschedules
    })),
  returnStoryToApproval: (storyId, payload) =>
    unwrap(axiosInstance.post(`/stories/${storyId}/return-to-approval`, payload)),

  // Instagram Accounts
  getInstagramAccounts: () => unwrap(axiosInstance.get('/accounts')),
  getInstagramAccount: (accountId) =>
    unwrap(axiosInstance.get(`/accounts/${accountId}`)),
  connectInstagramAccount: (payload) =>
    unwrap(axiosInstance.post('/accounts/connect', payload)),
  disconnectInstagramAccount: (accountId) =>
    unwrap(axiosInstance.delete(`/accounts/${accountId}`)),
  refreshInstagramToken: (accountId) =>
    unwrap(axiosInstance.post(`/accounts/${accountId}/refresh`)),

  // Account Management (Two-tier deletion)
  // Note: disconnectAccount is an alias for disconnectInstagramAccount
  disconnectAccount: (accountId) =>
    unwrap(axiosInstance.delete(`/accounts/${accountId}`)),
  scheduleAccountDeletion: (accountId, confirmation, reason = null) =>
    unwrap(axiosInstance.post(`/accounts/${accountId}/delete-permanent`, {
      confirmation_username: confirmation,
      reason
    })),
  recoverAccount: (accountId) =>
    unwrap(axiosInstance.post(`/accounts/${accountId}/recover`)),
  createStripePortalSession: (accountId) =>
    unwrap(axiosInstance.post('/stripe/portal', {
      account_id: accountId,
      return_url: `${window.location.origin}/accounts`
    })),

  // Instagram OAuth (Direct - new flow)
  // options.capi_context - Browser context (fbp, fbc, user_agent, url) for CAPI StartTrial
  // options.capi_event_id - Event ID for CAPI deduplication
  initiateInstagramOAuth: (options = {}) =>
    unwrap(axiosInstance.post('/instagram/oauth/init', {
      ...(options.capi_context && { capi_context: options.capi_context }),
      ...(options.capi_event_id && { capi_event_id: options.capi_event_id }),
    })),

  // Facebook OAuth (Legacy via Pages)
  // options.capi_context - Browser context (fbp, fbc, user_agent, url) for CAPI StartTrial
  // options.capi_event_id - Event ID for CAPI deduplication
  initiateFacebookOAuth: (options = {}) =>
    unwrap(axiosInstance.post('/facebook/oauth/init', {
      ...(options.capi_context && { capi_context: options.capi_context }),
      ...(options.capi_event_id && { capi_event_id: options.capi_event_id }),
    })),

  // Legacy method for backwards compatibility
  initiateOAuth: () =>
    unwrap(axiosInstance.post('/instagram/oauth/init')),

  // Token refresh (works for both providers)
  refreshToken: (accountId) =>
    unwrap(axiosInstance.post('/instagram/token/refresh', { account_id: accountId })),

  getAccountImages: (accountId, params = {}) =>
    unwrap(
      axiosInstance.get('/images', {
        params: {
          ...params,
          instagram_account_id: accountId,
        },
      })
    ),
  getAccountStories: (accountId, params = {}) =>
    unwrap(
      axiosInstance.get('/stories', {
        params: {
          ...params,
          instagram_account_id: accountId,
        },
      })
    ),
  getAccountStoryAnalytics: (accountId, params = {}) =>
    unwrap(
      axiosInstance.get('/analytics/stories', {
        params: {
          ...params,
          instagram_account_id: accountId,
        },
      })
    ),
  getAccountPerformanceAnalytics: (accountId, params = {}) =>
    unwrap(
      axiosInstance.get('/analytics/performance', {
        params: {
          ...params,
          instagram_account_id: accountId,
        },
      })
    ),

  // Analytics
  getStoryAnalytics: (params = {}) =>
    unwrap(axiosInstance.get('/analytics/stories', { params })),
  getPerformanceAnalytics: (params = {}) =>
    unwrap(axiosInstance.get('/analytics/performance', { params })),

  // Health check
  healthCheck: () => unwrap(axiosInstance.get('/health')),

  // Image Upload
  uploadImage: (formData, options = {}) => {
    return unwrap(
      axiosInstance.post('/images/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 120000, // 2 minutes for image uploads
        ...options
      })
    )
  },

  // Media Upload (Images + Videos)
  uploadMedia: (formData, options = {}) => {
    return unwrap(
      axiosInstance.post('/media/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 300000, // 5 minutes for large video uploads (100MB @ 3Mbit/s = ~4.5min)
        ...options
      })
    )
  },

  // Story Scheduling
  scheduleStory: (payload) => {
    return unwrap(axiosInstance.post('/stories/schedule', payload))
  },

  // Brand Settings
  getBrandSettings: (accountId, config = {}) =>
    unwrap(axiosInstance.get(`/accounts/${accountId}/brand-settings`, config)),
  updateBrandSettings: (accountId, settings) => {
    // Sanitize brand settings data
    const sanitizedSettings = {
      ...settings,
      // Sanitize TOV examples (array of objects with text property)
      ...(settings.tov_examples && {
        tov_examples: settings.tov_examples.map(example => ({
          ...example,
          text: sanitizeInput(example.text, 280)
        }))
      }),
      // Sanitize additional instructions
      ...(settings.additional_instructions && {
        additional_instructions: sanitizeInput(settings.additional_instructions, 500)
      }),
      // Sanitize color values
      ...(settings.text_color && { text_color: sanitizeHexColor(settings.text_color) }),
      ...(settings.background_color && { background_color: sanitizeHexColor(settings.background_color) }),
    }
    return unwrap(axiosInstance.put(`/accounts/${accountId}/brand-settings`, sanitizedSettings))
  },

  // Brand Profile Wizard
  getBrandProfile: (accountId, config = {}) =>
    unwrap(axiosInstance.get(`/accounts/${accountId}/brand-profile`, config)),

  updateBrandProfile: (accountId, profileData) =>
    unwrap(axiosInstance.put(`/accounts/${accountId}/brand-profile`, profileData)),

  getBrandProfileStatus: (accountId, config = {}) =>
    unwrap(axiosInstance.get(`/accounts/${accountId}/brand-profile/status`, config)),

  /**
   * Poll for brand profile processing completion.
   * @param {string} accountId - The account ID
   * @param {number} maxAttempts - Maximum polling attempts (default: 30)
   * @param {number} intervalMs - Polling interval in ms (default: 1000)
   * @returns {Promise<Object>} Final status object
   */
  pollBrandProfileStatus: async (accountId, maxAttempts = 30, intervalMs = 1000) => {
    for (let i = 0; i < maxAttempts; i++) {
      const status = await unwrap(
        axiosInstance.get(`/accounts/${accountId}/brand-profile/status`)
      )
      if (status.status === 'completed' || status.status === 'error') {
        return status
      }
      await new Promise(resolve => setTimeout(resolve, intervalMs))
    }
    throw new Error('Brand profile processing timeout')
  },

  // Subscription & Payments (Stripe)
  // options.capi_event_id - Event ID for CAPI deduplication
  // options.capi_context - Browser context (fbp, fbc, user_agent, url) for CAPI
  createAccountCheckoutSession: (accountId, planId, billingPeriod, options = {}) =>
    unwrap(axiosInstance.post('/stripe/checkout', {
      account_id: accountId,
      plan_id: planId,
      billing_period: billingPeriod,
      ...(options.capi_event_id && { capi_event_id: options.capi_event_id }),
      ...(options.capi_context && { capi_context: options.capi_context }),
    })),
  updateAccountSubscription: (accountId, newPlanId, newBillingPeriod) =>
    unwrap(axiosInstance.post('/stripe/subscription/update', {
      account_id: accountId,
      new_plan_id: newPlanId,
      new_billing_period: newBillingPeriod
    })),
  cancelAccountSubscription: (accountId, cancelAtPeriodEnd = true) =>
    unwrap(axiosInstance.post('/stripe/subscription/cancel', {
      account_id: accountId,
      cancel_at_period_end: cancelAtPeriodEnd
    })),
  getCheckoutSession: (sessionId) =>
    unwrap(axiosInstance.get(`/stripe/checkout/session/${sessionId}`)),
  getAccountSubscription: (accountId) =>
    unwrap(axiosInstance.get('/subscription/info', {
      params: { account_id: accountId }
    })),
  getSubscriptionPlans: () =>
    unwrap(axiosInstance.get('/subscription/plans')),

  // Add-ons Management
  createAddonCheckoutSession: (accountId, addonId) =>
    unwrap(axiosInstance.post('/addons/checkout', {
      account_id: accountId,
      addon_id: addonId
    })),
  removeAddon: (accountId, addonId) =>
    unwrap(axiosInstance.post('/addons/remove', {
      account_id: accountId,
      addon_id: addonId
    })),

  // ============================================
  // E-Commerce: Feeds
  // ============================================
  getFeeds: () => unwrap(axiosInstance.get('/ecommerce/feeds')),
  createFeed: (payload) => unwrap(axiosInstance.post('/ecommerce/feeds', payload)),
  getFeed: (feedId) => unwrap(axiosInstance.get(`/ecommerce/feeds/${feedId}`)),
  updateFeed: (feedId, payload) => unwrap(axiosInstance.put(`/ecommerce/feeds/${feedId}`, payload)),
  syncFeed: (feedId) => unwrap(axiosInstance.post(`/ecommerce/feeds/${feedId}/sync`)),
  syncReviews: (feedId) => unwrap(axiosInstance.post(`/ecommerce/feeds/${feedId}/sync-reviews`)),
  deleteFeed: (feedId, deleteProducts = false) =>
    unwrap(axiosInstance.delete(`/ecommerce/feeds/${feedId}`, { params: { delete_products: deleteProducts } })),
  validateFeed: (payload) => unwrap(axiosInstance.post('/ecommerce/feeds/validate', payload)),

  // ============================================
  // E-Commerce: Products
  // ============================================
  getProducts: (params = {}) => unwrap(axiosInstance.get('/ecommerce/products', { params })),
  getProduct: (productId) => unwrap(axiosInstance.get(`/ecommerce/products/${productId}`)),
  updateProduct: (productId, payload) => unwrap(axiosInstance.put(`/ecommerce/products/${productId}`, payload)),
  bulkUpdateProducts: (productIds, payload) =>
    unwrap(axiosInstance.put('/ecommerce/products/bulk', { product_ids: productIds, ...payload })),
  useProductInStory: (productId, payload) =>
    unwrap(axiosInstance.post(`/ecommerce/products/${productId}/use-in-story`, payload)),
  renderProductVideo: (productId, payload) =>
    unwrap(axiosInstance.post(`/ecommerce/products/${productId}/render-video`, payload)),
  useExistingVideo: (productId, payload = {}) =>
    unwrap(axiosInstance.post(`/ecommerce/products/${productId}/use-existing-video`, payload)),
  /**
   * Get available video templates for a product with eligibility info.
   * Returns templates with available: true/false based on product data.
   */
  getProductTemplates: (productId) =>
    unwrap(axiosInstance.get(`/ecommerce/products/${productId}/templates`)),

  // ============================================
  // E-Commerce: Video Templates
  // ============================================
  getVideoTemplates: () => unwrap(axiosInstance.get('/ecommerce/templates')),
  getVideoTemplate: (templateId) => unwrap(axiosInstance.get(`/ecommerce/templates/${templateId}`)),
  generateTemplatePreview: (templateId, payload) =>
    unwrap(axiosInstance.post(`/ecommerce/templates/${templateId}/preview`, payload)),

  // ============================================
  // E-Commerce: Product Proposals (Approval Queue)
  // ============================================
  /**
   * Create a product proposal for the approval queue.
   * Generates a preview image and adds it to the approval queue.
   */
  createProductProposal: (productId, payload) => {
    const sanitizedPayload = {
      ...payload,
      ...(payload.caption_text && { caption_text: sanitizeCaption(payload.caption_text) }),
    }
    return unwrap(axiosInstance.post(`/ecommerce/products/${productId}/create-proposal`, sanitizedPayload, { timeout: 120000 }))
  },

  /**
   * Approve a product proposal and start video rendering.
   * Returns immediately while video renders async (~2-3 min).
   */
  approveProductProposal: (proposalId, payload = {}) => {
    const sanitizedPayload = {
      ...payload,
      ...(payload.caption_text && { caption_text: sanitizeCaption(payload.caption_text) }),
    }
    return unwrap(axiosInstance.post(`/ecommerce/proposals/${proposalId}/approve`, sanitizedPayload))
  },

  /**
   * Reject a product proposal and remove from approval queue.
   */
  rejectProductProposal: (proposalId, payload = {}) => {
    const sanitizedPayload = {
      ...payload,
      ...(payload.reason && { reason: sanitizeInput(payload.reason, 500) }),
    }
    return unwrap(axiosInstance.post(`/ecommerce/proposals/${proposalId}/reject`, sanitizedPayload))
  },

  /**
   * Get a single product proposal by ID.
   */
  getProductProposal: (proposalId) =>
    unwrap(axiosInstance.get(`/ecommerce/proposals/${proposalId}`)),

  // ============================================
  // E-Commerce: Bio Pages
  // ============================================
  getBioPages: () => unwrap(axiosInstance.get('/ecommerce/bio-pages')),
  createBioPage: (payload) => unwrap(axiosInstance.post('/ecommerce/bio-pages', payload)),
  getBioPage: (pageId) => unwrap(axiosInstance.get(`/ecommerce/bio-pages/${pageId}`)),
  updateBioPage: (pageId, payload) => unwrap(axiosInstance.put(`/ecommerce/bio-pages/${pageId}`, payload)),
  deleteBioPage: (pageId) => unwrap(axiosInstance.delete(`/ecommerce/bio-pages/${pageId}`)),
  publishBioPage: (pageId, isPublished) =>
    unwrap(axiosInstance.post(`/ecommerce/bio-pages/${pageId}/publish`, { is_published: isPublished })),
  getBioPageAnalytics: (pageId, params = {}) =>
    unwrap(axiosInstance.get(`/ecommerce/bio-pages/${pageId}/analytics`, { params })),

  // ============================================
  // E-Commerce: Analytics
  // ============================================
  getClickAnalytics: (params = {}) => unwrap(axiosInstance.get('/ecommerce/analytics/clicks', { params })),
  getProductPerformance: (params = {}) => unwrap(axiosInstance.get('/ecommerce/analytics/products', { params })),

  // ============================================
  // E-Commerce: Usage / Quotas
  // ============================================
  getEcommerceUsage: () => unwrap(axiosInstance.get('/ecommerce/usage')),
}

// Export the axios instance for custom requests
export default axiosInstance
